import React, { useState, useEffect } from 'react';
import { Typography, Table, Button, Modal, Form, Input, Space, Alert, Spin, Switch } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getUsers, updateUserRole, deleteUser } from '../services/usersService';

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const { Title } = Typography;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      form.setFieldsValue({
        email: user.email,
        isAdmin: user.user_metadata?.isAdmin,
      });
    } else {
      setEditingUser(null);
      form.resetFields();
    }
    setOpenDialog(true);
  };

  const handleCloseModal = () => {
    setOpenDialog(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleSubmit = async values => {
    setLoading(true);
    try {
      if (editingUser) {
        await updateUserRole(editingUser.id, values.isAdmin);
      }
      fetchUsers();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: "Are you sure you want to remove this user's admin access?",
      onOk: async () => {
        setLoading(true);
        try {
          await deleteUser(id);
          fetchUsers();
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Admin Status',
      dataIndex: ['user_metadata', 'isAdmin'],
      key: 'isAdmin',
      render: isAdmin => <Switch checked={!!isAdmin} disabled />,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: date => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  if (error) return <Alert message={error} type="error" />;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2}>Users</Title>
      </div>

      <Table columns={columns} dataSource={users} rowKey="id" />

      <Modal
        title="Edit User Admin Status"
        open={openDialog}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="email" label="Email">
            <Input disabled />
          </Form.Item>
          <Form.Item name="isAdmin" label="Admin Access" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default UsersAdmin;
